import React, { useState } from "react";
import { Box, Button, FormHelperText, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FileModel } from "../models/models";
import colors from "../themes/colors";
import useTranslation from "../hooks/useTranslation";

interface UploadFileProps {
  variant?: "default" | "image" | "pdf";
  file: FileModel | null;
  onChangeFile: (file: FileModel | null) => void;
  maxFileSizeMb?: number;
}

const UploadFile = ({ file, variant = "default", onChangeFile, maxFileSizeMb = 5 }: UploadFileProps) => {
  const [canDrop, setCanDrop] = useState(false);
  const [error, setError] = useState<string>();
  const { translate } = useTranslation();

  const handleFileChange = (event: any) => {
    processFiles(event.target.files as FileList);
  };

  const handleFileDrop = (event: any) => {
    event.preventDefault();
    processFiles(event.dataTransfer.files as FileList);
  };

  const handleClearInput = () => {
    onChangeFile(null);
  };

  const processFiles = (filesList: FileList) => {
    setError(undefined);

    if (filesList[0].size > maxFileSizeMb * 1024 * 1024) {
      onChangeFile?.(null);
      setError(translate("uploadFile.fileIsTooBig"));
      return;
    }

    detectUrl(filesList[0]).then((url) => {
      if (url) {
        const file = {
          name: filesList[0].name,
          content: url
        };

        onChangeFile(file);
      }
    });
  };

  const detectUrl = (file: Blob | null) => {
    return new Promise<string | null>((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
      };
    });
  };

  const getUploadButtonText = (variant: string) => {
    switch (variant) {
      case "default":
        return translate("uploadFile.file");
      case "image":
        return translate("uploadFile.image");
      case "pdf":
        return translate("uploadFile.pdfFile");
    }
  };

  const getFileFormat = (variant: string) => {
    switch (variant) {
      case "default":
        return "";
      case "image":
        return ".jpeg, .jpg, .png, .bmp, .tiff";
      case "pdf":
        return ".pdf";
    }
  };

  const showCloseIcon = file
    ? {
      endAdornment: (
        <IconButton onClick={handleClearInput}>
          <CloseIcon />
        </IconButton>
      )
    }
    : {};

  return (
    <div className="flex gap-4">
      {(!file || variant === "default") && (
        <Box
          className={`upload-file w-full ${canDrop && "active"}`}
          sx={{
            borderColor: !!error ? colors.primary.red : ""
          }}
          onDrop={(event) => {
            handleFileDrop(event);
            setCanDrop(false);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setCanDrop(true);
          }}
          onDragLeave={() => setCanDrop(false)}
        >
          <p className="text-uppercase text-center text-12-regular">{translate("uploadFile.dragAndDrop")}</p>
          <p className="text-uppercase text-center text-12-regular">{translate("uploadFile.or")}</p>

          <label htmlFor="file-input">
            <Button component="span" variant="outlined">
              {translate("uploadFile.upload")} {getUploadButtonText(variant)}
            </Button>
          </label>

          {!!error && (
            <FormHelperText
              error
              sx={{
                padding: "6px 8px",
                margin: 0,
                textAlign: "center",
                minHeight: "40px",
                maxWidth: "370px"
              }}
            >
              {error}
            </FormHelperText>
          )}
        </Box>
      )}

      <TextField
        type="file"
        id="file-input"
        style={{ display: "none" }}
        onChange={handleFileChange}
        onClick={(event: any) => {
          event.target.value = null;
        }}
        inputProps={{ accept: getFileFormat(variant) }}
      ></TextField>

      {file && variant === "image" && (
        <div className="w-full h-auto">
          <img className="aspect-square object-cover w-full" src={file.content} alt="" />
        </div>
      )}

      {file && (
        <div className="w-full">
          <TextField type="text" className="w-full" value={file.name || ""} InputProps={showCloseIcon}></TextField>
        </div>
      )}
    </div>
  );
};
export default UploadFile;
